import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { usersAPI } from "../services/api";
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  TextField, Alert, CircularProgress
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import LockIcon from "@mui/icons-material/Lock";

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (form.new_password !== form.confirm_password) {
      setError("New password and confirm password do not match.");
      return;
    }

    if (form.new_password.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      await usersAPI.changePassword(form);
      setSuccess("Password changed successfully! Please login again with your new password.");
      setTimeout(() => logout(), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <AppBar position="static" sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
        <Toolbar>
          <img src="/alteram-logo.png" alt="Alteram" style={{ height: 36, objectFit: "contain", marginRight: 8, backgroundColor: "white", padding: "3px 6px", borderRadius: 4 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Alteram Contact Centre Training
          </Typography>
          <Button color="inherit" onClick={logout}>Sign Out</Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 4, maxWidth: 500, mx: "auto" }}>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: "50%",
                bgcolor: "#e8eaf6", display: "flex",
                alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2
              }}>
                <LockIcon sx={{ fontSize: 32, color: "#1a237e" }} />
              </Box>
              <Typography variant="h5" fontWeight={700} color="#1a237e">
                Change Password
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                {user?.full_name} — {user?.email}
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={form.current_password}
                onChange={e => setForm({...form, current_password: e.target.value})}
                required
                sx={{ mb: 2 }}
                helperText="If you have never changed your password, use: changeme"
              />
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={form.new_password}
                onChange={e => setForm({...form, new_password: e.target.value})}
                required
                sx={{ mb: 2 }}
                helperText="Minimum 8 characters"
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={form.confirm_password}
                onChange={e => setForm({...form, confirm_password: e.target.value})}
                required
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  background: "linear-gradient(135deg, #1a237e, #0d47a1)",
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Change Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
